import React from "react";
import { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/app/DatePicker";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MISSION_TYPES } from "@/lib/labels";
import { toast } from "sonner";
import { Plus, PencilSimple } from "@phosphor-icons/react";

const EMPTY = {
  client_id: "", title: "", mission_type: "tenue_comptable",
  start_date: "", end_date: "", fees_ht: "", description: "",
};

export function MissionFormDialog({ clients, mission = null }) {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState(EMPTY);

  const onOpenChange = (v) => {
    if (v && mission) {
      setForm({
        client_id: mission.client_id, title: mission.title, mission_type: mission.mission_type,
        start_date: mission.start_date || "", end_date: mission.end_date || "",
        fees_ht: mission.fees_ht ?? "", description: mission.description || "",
      });
    }
    setOpen(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, fees_ht: form.fees_ht !== "" ? parseFloat(form.fees_ht) : null };
      if (mission) {
        await api.put(`/missions/${mission.id}`, payload);
        toast.success("Mission mise à jour");
      } else {
        await api.post("/missions", payload);
        toast.success("Mission créée");
      }
      setOpen(false);
      setForm(EMPTY);
      mutate("/missions");
    } catch { toast.error(mission ? "Mise à jour impossible" : "Création impossible"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {mission ? (
          <Button size="sm" variant="ghost" title="Modifier la mission (honoraires, dates…)" data-testid={`edit-mission-btn-${mission.id}`}>
            <PencilSimple size={15} className="text-[#2E3192]" />
          </Button>
        ) : (
          <Button data-testid="new-mission-btn" className="bg-[#2E3192] hover:bg-[#1E2066]"><Plus size={16} className="mr-1" /> Nouvelle mission</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl" data-testid="mission-form-title">
            {mission ? `Modifier — ${mission.title}` : "Nouvelle mission"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Client *</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })} disabled={!!mission}>
              <SelectTrigger data-testid="mission-client"><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label>Intitulé *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="mission-title" /></div>
          <div>
            <Label>Type *</Label>
            <Select value={form.mission_type} onValueChange={(v) => setForm({ ...form, mission_type: v })}>
              <SelectTrigger data-testid="mission-type"><SelectValue /></SelectTrigger>
              <SelectContent>{MISSION_TYPES.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div><Label>Honoraires HT</Label><Input type="number" step="0.01" value={form.fees_ht} onChange={(e) => setForm({ ...form, fees_ht: e.target.value })} data-testid="mission-fees" /></div>
          <div><Label>Date début</Label><DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="mission-start-date" /></div>
          <div><Label>Date fin</Label><DatePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testid="mission-end-date" /></div>
          <div className="md:col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="submit-mission" disabled={!form.client_id}>{mission ? "Enregistrer" : "Créer"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
